import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, forkJoin, Observable, timer, Subscription } from 'rxjs';
import { TableV2Column } from '../table-v2/table-v2.interface';
import { fiscalShiftDuration } from './fiscal-shift-duration';
import { VchasnoKasaService } from '../../services/vchasno-kasa.service';
import { BoardPaymentService } from '../../services/board-payment.service';
import { StackedSidebarService } from '../../services/stacked-sidebar.service';
import { SidebarType } from '../layout/stacked-sidebar/stacked-sidebar.interface';
import { TranslationManagerService } from '../../core/i18n';
import { WebSocketService } from '../../services/gateway.service';
import { FiscalNotificationService } from '../../services/fiscal-notification.service';
import {
  CashRegister,
  FISCAL_STATUSES,
  FiscalOperation,
  FiscalSettings,
  FiscalMapping,
  VCHASNO_PAYMENT_CODES,
  VCHASNO_TAX_GROUP_OPTIONS,
  FiscalShift,
  RegisterStatus,
} from './prro.types';

@Component({
  selector: 'app-prro',
  standalone: false,
  templateUrl: './prro.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'block h-full min-h-0' },
})
export class PrroComponent implements OnInit {
  @Input() configuration = false;
  @Output() close = new EventEmitter<void>();
  private readonly api = inject(VchasnoKasaService);
  private readonly sidebar = inject(StackedSidebarService);
  private readonly feedback = inject(BoardPaymentService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ws = inject(WebSocketService);
  private readonly fiscalNotifications = inject(FiscalNotificationService);
  protected readonly tabs = ['registers', 'operations', 'shifts'];
  protected readonly statuses = FISCAL_STATUSES.map((id) => ({ id, name: `prro.status.${id}` }));
  protected readonly shiftStatuses = ['open', 'closed', 'unknown'].map((id) => ({
    id,
    name: `prro.shift_status.${id}`,
  }));
  protected readonly tasks = [0, 1, 2, 3, 4, 10, 11, 18].map((id) => ({ id, name: `prro.task.${id}` }));
  private readonly standardPaymentOptions = VCHASNO_PAYMENT_CODES.map((id) => ({
    id,
    name: `prro.payment_methods.${id}`,
  }));
  protected get paymentOptions(): { id: number; name: string; disabled?: boolean }[] {
    const standardCodes = new Set<number>(VCHASNO_PAYMENT_CODES);
    const savedCustomCodes = [...new Set(this.settings.payment_mappings.map((mapping) => mapping.payment_code))].filter(
      (code) => !standardCodes.has(code),
    );
    return [
      ...this.standardPaymentOptions,
      ...savedCustomCodes.map((id) => ({ id, name: String(id) })),
    ];
  }
  protected setPaymentCode(mapping: FiscalMapping, code: number): void {
    if (code === 2) mapping.requires_card_metadata = true;
    else if (mapping.payment_code === 2) mapping.requires_card_metadata = false;
    mapping.payment_code = code;
  }
  protected readonly taxOptions = VCHASNO_TAX_GROUP_OPTIONS;
  protected readonly operationColumns: TableV2Column<FiscalOperation>[] = [
    { key: 'register', header: 'prro.register' },
    { key: 'date', header: 'prro.date' },
    { key: 'status', header: 'prro.state' },
    { key: 'receipt', header: 'prro.receipt' },
    { key: 'details', header: 'actions', align: 'right', class: 'w-28', nowrap: true },
  ];
  protected readonly shiftColumns: TableV2Column<FiscalShift>[] = [
    { key: 'register', header: 'prro.register' },
    { key: 'date', header: 'prro.date' },
    { key: 'status', header: 'prro.state' },
    { key: 'duration', header: 'prro.shift_duration', nowrap: true },
    { key: 'details', header: 'prro.details' },
  ];
  protected readonly shiftDuration = fiscalShiftDuration;
  protected tab = 'registers';
  protected registers: CashRegister[] = [];
  protected archivedRegisters: CashRegister[] = [];
  protected showArchived = false;
  protected archiveLoading = false;
  protected archiveError = '';
  protected settings: FiscalSettings = { default_tax_group: null, payment_mappings: [] };
  protected typeOptions: { id: string; name: string }[] = [];
  protected serviceOptions: { id: string; name: string }[] = [];
  protected loading = true;
  protected error = '';
  protected busy = false;
  protected registerId: string | null = null;
  protected statusFilter: string | null = null;
  protected shiftStatus: string | null = null;
  protected task: number | null = null;
  protected operations: FiscalOperation[] = [];
  protected shifts: FiscalShift[] = [];
  protected page = 1;
  protected total = 0;
  protected journalLoading = false;
  protected journalError = '';
  protected statusesById: Record<string, RegisterStatus> = {};
  protected statusBusy = new Set<string>();
  private journalInFlight = false;
  private journalRequest?: Subscription;
  private loadRequest?: Subscription;
  private archiveRequest?: Subscription;
  protected readonly trackById = (row: { id: string }): string => row.id;
  private readonly commandKeys = new Map<string, string>();

  ngOnInit(): void {
    injectTranslations(this.destroyRef, this.translationManager);
    if (this.configuration) {
      this.translationManager.loadModuleTranslations('payments').pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
    this.load();
    this.ws.fiscalOperationCompleted()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (this.configuration || this.loading) return;
        this.loadJournal(false, true);
        this.refreshStatus(event.cash_register_id, false);
      });
    timer(10000, 10000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.configuration && !this.loading && !this.error && !this.busy) {
          this.loadJournal(false);
          if (this.tab === 'registers')
            this.registers.filter((r) => r.is_active).forEach((r) => this.refreshStatus(r.id, false));
        }
      });
  }
  private readonly translationManager = inject(TranslationManagerService);
  protected load(showLoading = true): void {
    this.loadRequest?.unsubscribe();
    if (showLoading) {
      this.loading = true;
      this.error = '';
    }
    this.loadRequest = forkJoin({ registers: this.api.registers(), settings: this.api.settings() })
      .pipe(
        finalize(() => {
          if (showLoading) this.loading = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ registers, settings }) => {
          this.registers = registers;
          this.settings = settings;
          if (this.configuration) {
            this.loadDirectories();
            if (settings.can_manage && this.showArchived) this.loadArchive();
            if (!settings.can_manage) {
              this.showArchived = false;
              this.archivedRegisters = [];
            }
          } else {
            this.loadJournal(showLoading, true);
            registers.filter((r) => r.is_active).forEach((r) => this.refreshStatus(r.id, showLoading));
          }
        },
        error: (e) => {
          if (showLoading) this.error = this.message(e);
          else this.feedback.error(e);
        },
      });
  }
  private loadDirectories(): void {
    forkJoin({ types: this.feedback.getPaymentTypes(), services: this.feedback.getPaymentServices() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => {
          this.typeOptions = r.types;
          this.serviceOptions = r.services;
        },
        error: (e) => (this.error = this.message(e)),
      });
  }
  protected selectTab(tab: string): void {
    this.tab = tab;
    this.page = 1;
    this.loadJournal();
  }
  protected filter(): void {
    this.page = 1;
    this.loadJournal();
  }
  protected loadJournal(showLoading = true, replaceInFlight = false): void {
    if (!showLoading && this.journalInFlight && !replaceInFlight) return;
    this.journalRequest?.unsubscribe();
    this.journalInFlight = true;
    this.journalLoading = showLoading;
    if (showLoading) this.journalError = '';
    const query = { cash_register_id: this.registerId, page: this.page, take: 25 };
    const request: Observable<
      import('./prro.types').FiscalPage<FiscalOperation> | import('./prro.types').FiscalPage<FiscalShift>
    > =
      this.tab === 'shifts'
        ? this.api.shifts({ ...query, status: this.shiftStatus })
        : this.api.operations({ ...query, status: this.statusFilter, task: this.task });
    this.journalRequest = request
      .pipe(
        finalize(() => {
          this.journalLoading = false;
          this.journalInFlight = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          if (this.tab === 'shifts') this.shifts = result.items as FiscalShift[];
          else this.operations = result.items as FiscalOperation[];
          this.total = result.total;
          this.journalError = '';
        },
        error: (e) => (this.journalError = this.message(e)),
      });
  }
  protected changePage(step: number): void {
    this.page += step;
    this.loadJournal();
  }
  protected registerName(id: string): string {
    return this.registers.find((r) => r.id === id)?.name ?? id;
  }
  protected formatOpenedAt(value: string | null): string {
    if (!value) return '—';
    const parts = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/.exec(value);
    return parts ? `${parts[3]}.${parts[2]}.${parts[1]} ${parts[4]}:${parts[5]}:${parts[6]}` : value;
  }
  protected refreshStatus(id: string, notify = true): void {
    if (this.statusBusy.has(id)) return;
    this.statusBusy.add(id);
    this.api
      .status(id)
      .pipe(
        finalize(() => this.statusBusy.delete(id)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (value) => {
          this.statusesById[id] = value;
          if (value.result.error && notify) {
            this.fiscalNotifications.show({ kind: 'fiscal_operation', code: 'FISCAL_STATUS_CHECK_FAILED',
              params: { reason_code: (value.result.error as { code?: string }).code ?? null } });
          }
        },
        error: (e) => {
          delete this.statusesById[id];
          if (notify) this.feedback.error(e);
        },
      });
  }
  protected command(register: CashRegister, action: string): void {
    const run = () => {
      const key = `${register.id}:${action}`;
      if (!this.commandKeys.has(key)) this.commandKeys.set(key, crypto.randomUUID());
      this.mutate(this.api.command(register.id, action, this.commandKeys.get(key)!), () => {
        this.commandKeys.delete(key);
        this.loadJournal();
      });
    };
    if (action === 'close')
      this.feedback.confirm('prro.close_confirm').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(run);
    else run();
  }
  protected openSettings(): void {
    if (this.loading || this.busy) return;
    this.sidebar
      .openSidebar<void>({ type: SidebarType.VchasnoKasaSettings, width: '760px' }, {})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load(false));
  }
  protected openForm(register: CashRegister | null): void {
    if (!this.settings.can_manage || this.busy) return;
    this.sidebar
      .openSidebar<CashRegister>({ type: SidebarType.VchasnoKasaRegisterForm, width: '520px' }, { register })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((saved) => {
        if (saved) this.load(false);
      });
  }
  protected toggle(register: CashRegister, isActive: boolean): void {
    if (this.busy || !this.settings.can_manage || register.is_active === isActive) return;
    const previous = register.is_active;
    register.is_active = isActive;
    this.busy = true;
    this.api
      .saveRegister(register.id, { name: register.name, is_active: isActive })
      .pipe(
        finalize(() => (this.busy = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.load(),
        error: (error) => {
          register.is_active = previous;
          this.feedback.error(error);
        },
      });
  }
  protected selectRegisterView(archived: boolean): void {
    if (this.busy || !this.settings.can_manage) return;
    this.showArchived = archived;
    if (archived) this.loadArchive();
  }
  protected loadArchive(): void {
    if (!this.settings.can_manage) return;
    this.archiveRequest?.unsubscribe();
    this.archiveLoading = true;
    this.archiveError = '';
    this.archiveRequest = this.api.registers(true)
      .pipe(
        finalize(() => (this.archiveLoading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (registers) => (this.archivedRegisters = registers),
        error: (error) => (this.archiveError = this.message(error)),
      });
  }
  protected restore(register: CashRegister): void {
    if (this.busy || !this.settings.can_manage) return;
    this.mutate(this.api.restoreRegister(register.id), () => {
      this.feedback.success('prro.restored');
      this.load();
    });
  }
  protected archive(register: CashRegister): void {
    if (this.busy || !this.settings.can_manage) return;
    this.feedback
      .confirm('prro.archive_confirm', { name: register.name })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.mutate(this.api.archiveRegister(register.id), () => this.load()));
  }
  protected addMapping(): void {
    this.settings.payment_mappings.push({
      payment_type_id: '',
      payment_service_id: null,
      payment_code: 0,
      requires_card_metadata: false,
    });
  }
  protected saveSettings(): void {
    if (
      this.settings.payment_mappings.some(
        (m) => !m.payment_type_id || !Number.isInteger(Number(m.payment_code)) || Number(m.payment_code) < 0,
      )
    ) {
      this.feedback.error({ code: 'INVALID_FISCAL_SETTINGS' });
      return;
    }
    const settings = {
      ...this.settings,
      payment_mappings: this.settings.payment_mappings.map((m) => ({
        ...m,
        payment_code: Number(m.payment_code),
        requires_card_metadata: m.payment_code === 2 || m.requires_card_metadata,
      })),
    };
    this.mutate(this.api.saveSettings(settings), (result) => {
      this.settings = { ...result, can_manage: true };
      this.feedback.success('prro.saved');
    });
  }
  protected inspect(operation: FiscalOperation): void {
    this.sidebar
      .openSidebar<boolean>(
        { type: SidebarType.VchasnoKasaOperationDetail, width: '680px' },
        { operationId: operation.id, registerName: this.registerName(operation.cash_register_id) },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((changed) => {
        if (changed) this.loadJournal(false, true);
      });
  }
  protected showHistory(registerId: string, shiftId: string | null = null): void {
    this.sidebar
      .openSidebar(
        { type: SidebarType.VchasnoKasaHistory, width: '760px' },
        { registerId, shiftId, registerName: this.registerName(registerId) },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadJournal(false, true));
  }
  private mutate<T>(request: Observable<T>, next: (value: T) => void): void {
    if (this.busy) return;
    this.busy = true;
    request
      .pipe(
        finalize(() => (this.busy = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({ next, error: (e) => this.feedback.error(e) });
  }
  private message(error: any): string {
    return error?.status === 403 ? 'prro.denied' : 'prro.load_failed';
  }
}
function injectTranslations(destroyRef: DestroyRef, manager: TranslationManagerService): void {
  manager.loadModuleTranslations('prro').pipe(takeUntilDestroyed(destroyRef)).subscribe();
}
